import React, { useEffect, useState } from "react";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Upload, ExternalLink } from "lucide-react";

export default function AdvertisersPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [form, setForm] = useState({
    title: "",
    subtitle: "",
    target_url: "https://",
    image: "",
    lookup_key: "banner_weekly",
  });
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [mine, setMine] = useState([]);

  useEffect(() => {
    api.get("/plans").then((r) => {
      const isBusiness = user?.account_type === "empresa";
      const filtered = r.data.filter((p) => p.kind === "banner" && p.audience === (isBusiness ? "empresa" : "particular"));
      setPlans(filtered);
      if (filtered.length > 0) {
        setForm((f) => ({ ...f, lookup_key: filtered[0].lookup_key }));
      }
    });
  }, [user]);

  useEffect(() => {
    if (user && user.id) {
      api.get("/banners/mine").then((r) => setMine(r.data));
    }
  }, [user]);

  useEffect(() => {
    if (!authLoading && !(user && user.id)) {
      toast.error("Inicia sesión para contratar un banner");
      navigate("/");
    }
  }, [authLoading, user, navigate]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("image", data.path);
      toast.success("Imagen subida");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (uploading) return toast.error("Espera a que se suba la imagen");
    if (!form.image) return toast.error("Sube la imagen del banner");
    setBusy(true);
    try {
      const { data: banner } = await api.post("/banners", form);
      const { data: co } = await api.post("/payments/checkout", {
        lookup_key: form.lookup_key,
        origin_url: window.location.origin,
        banner_id: banner.id,
      });
      window.location.href = co.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error");
      setBusy(false);
    }
  };

  if (authLoading || !(user && user.id)) return null;

  return (
    <div className="bg-[#F8FAFC] min-h-screen">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14 lg:py-20 grid lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-5 lg:sticky lg:top-24 self-start">
          <p className="eyebrow mb-3">
            Espacio publicitario · {user?.account_type === "empresa" ? "Empresas" : "Particulares"}
          </p>
          <h1 className="font-serif text-4xl lg:text-5xl font-semibold text-[#1A1A1A] leading-tight">
            Aparece en portada y páginas de listado.
          </h1>
          <p className="mt-4 text-[#475569] leading-relaxed">
            Sube tu banner, elige la duración y activa tu campaña con un pago único.
            {user?.account_type === "empresa"
              ? " Tarifas para empresas con mayor visibilidad."
              : " Tarifas reducidas para particulares."}
          </p>
          <div className="mt-6 space-y-3">
            {plans.map((p) => (
              <button
                key={p.lookup_key}
                type="button"
                data-testid={`plan-${p.lookup_key}`}
                onClick={() => set("lookup_key", p.lookup_key)}
                className={`w-full text-left border p-4 flex items-baseline justify-between transition-colors ${
                  form.lookup_key === p.lookup_key
                    ? "border-[#3B5FB8] bg-white"
                    : "border-[#E2E8F0] bg-white hover:border-[#3B5FB8]"
                }`}
              >
                <div>
                  <p className="font-serif text-xl font-semibold text-[#1A1A1A]">{p.label}</p>
                  <p className="text-xs font-mono uppercase tracking-widest text-[#3B5FB8]">{p.interval}</p>
                </div>
                <div className="text-right">
                  <p className="font-serif text-2xl font-semibold text-[#1A1A1A]">{p.amount.toFixed(0)} €</p>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="lg:col-span-7">
          <form onSubmit={submit} className="space-y-6" data-testid="banner-form">
            <div className="bg-white border border-[#E2E8F0] p-6 space-y-4">
              <p className="eyebrow">Creativo</p>
              <div>
                <Label>Título del banner</Label>
                <Input
                  data-testid="banner-title"
                  value={form.title}
                  onChange={(e) => set("title", e.target.value)}
                  required
                  minLength={3}
                  maxLength={80}
                  placeholder="Escuela X · Cursos de verano"
                  className="rounded-none h-11 border-[#CBD5E1] mt-1.5"
                />
              </div>
              <div>
                <Label>Subtítulo (opcional)</Label>
                <Input
                  data-testid="banner-subtitle"
                  value={form.subtitle}
                  onChange={(e) => set("subtitle", e.target.value)}
                  maxLength={140}
                  placeholder="Inscripciones abiertas hasta el 30 de junio"
                  className="rounded-none h-11 border-[#CBD5E1] mt-1.5"
                />
              </div>
              <div>
                <Label>URL de destino</Label>
                <Input
                  data-testid="banner-url"
                  type="url"
                  value={form.target_url}
                  onChange={(e) => set("target_url", e.target.value)}
                  required
                  className="rounded-none h-11 border-[#CBD5E1] mt-1.5"
                />
              </div>
              <div>
                <Label>Imagen del banner (recomendado 1600x400)</Label>
                {form.image ? (
                  <div className="mt-1.5 border border-[#E2E8F0] overflow-hidden aspect-[4/1] bg-[#EEF2F7]">
                    <img src={fileUrl(form.image)} alt="preview" className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <label className="mt-1.5 border border-dashed border-[#CBD5E1] p-8 flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-[#3B5FB8] hover:bg-[#EEF2F7]" data-testid="banner-image-upload">
                    {uploading ? (
                      <span className="text-sm text-[#475569]">Subiendo...</span>
                    ) : (
                      <>
                        <Upload className="w-6 h-6 text-[#3B5FB8]" />
                        <span className="text-sm text-[#475569]">Sube una imagen del banner</span>
                      </>
                    )}
                    <input type="file" accept="image/*" className="hidden" onChange={onFile} disabled={uploading} />
                  </label>
                )}
                {form.image && (
                  <button
                    type="button"
                    onClick={() => set("image", "")}
                    className="mt-2 text-xs text-[#3B5FB8] link-underline"
                  >
                    Cambiar imagen
                  </button>
                )}
              </div>
            </div>

            <Button
              type="submit"
              data-testid="banner-checkout"
              disabled={busy || uploading}
              className="w-full h-14 rounded-none bg-[#1A1A1A] hover:bg-[#2A2A2A] text-[#F8FAFC] font-medium text-base"
            >
              {busy ? "Redirigiendo a Stripe..." : "Pagar y activar banner"}
            </Button>
            <p className="text-xs text-[#64748B]">Pago seguro con Stripe. Puedes usar la tarjeta de prueba 4242 4242 4242 4242 en modo test.</p>
          </form>

          {mine.length > 0 && (
            <div className="mt-10">
              <p className="eyebrow mb-3">Mis banners</p>
              <div className="space-y-2">
                {mine.map((b) => (
                  <div key={b.id} className="bg-white border border-[#E2E8F0] p-4 flex items-center gap-4">
                    <div className="w-24 h-14 bg-[#EEF2F7] overflow-hidden flex-shrink-0">
                      {b.image && <img src={fileUrl(b.image)} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-serif text-lg font-semibold text-[#1A1A1A] truncate">{b.title}</p>
                      <p className="text-xs text-[#475569]">
                        {b.active ? `Activo hasta ${new Date(b.expires_at).toLocaleDateString("es-ES")}` : "Pendiente de pago"}
                      </p>
                    </div>
                    {b.target_url && (
                      <a
                        href={b.target_url}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="text-[#3B5FB8] flex items-center gap-1 text-sm link-underline"
                      >
                        Enlace <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
